import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { buildContextPath } from "config/contextModules";
import { useLocationContext } from "context/LocationContext";
import { loadKpiTrackerRecords } from "utils/kpiTrackerSource";

function formatCell(value) {
  const text = String(value || "").trim();
  if (!text) return "—";
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!isoMatch) return text;
  const parsed = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return text;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function KpiDataSourceButton({ kpiText, scoringText = "", year }) {
  const navigate = useNavigate();
  const { currentLocation } = useLocationContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const orgUnitId = currentLocation?.orgUnitId || "";
  const locationName = currentLocation?.name || "the selected organisation unit";

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let isActive = true;
    setIsLoading(true);
    setError("");
    setResult(null);

    loadKpiTrackerRecords({ kpiText, scoringText, year, orgUnitId })
      .then((nextResult) => {
        if (isActive) setResult(nextResult);
      })
      .catch((loadError) => {
        console.error("Failed to load KPI tracker data:", loadError);
        if (isActive) setError("The tracker records could not be loaded for this organisation unit.");
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isOpen, kpiText, orgUnitId, scoringText, year]);

  const source = result?.source || null;

  const openTrackers = () => {
    if (!result?.linked) return;
    const observedMeetingTypes = source?.kind === "meetings"
      ? [...new Set((result?.rows || []).map((row) => row.cells?.["Meeting type"] || row.cells?.["DPAT | Meeting Type"]).filter(Boolean))]
      : [];
    const searchQuery = observedMeetingTypes.length > 0 ? observedMeetingTypes.join("|") : source?.searchQuery;
    const queryMode = observedMeetingTypes.length > 0 ? "" : source?.qMode;
    const params = new URLSearchParams();
    if (result?.programId) params.set("tracker", result.programId);
    if (searchQuery) params.set("q", searchQuery);
    if (queryMode) params.set("qMode", queryMode);
    if (source?.year) params.set("year", source.year);
    navigate(`${buildContextPath(currentLocation, "trackers")}?${params.toString()}`);
  };

  const columns = (result?.columns || []).filter((column) =>
    (result?.rows || []).some((row) => String(row.cells?.[column.label] || "").trim()),
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="text-xs font-semibold text-[#16325a] underline-offset-2 hover:underline"
      >
        Data source
      </button>

      {isOpen
        ? createPortal(
            <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
              <button
                type="button"
                aria-label="Close data source"
                className="absolute inset-0 bg-slate-950/45"
                onClick={() => setIsOpen(false)}
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="kpi-data-source-title"
                className="relative flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
              >
                <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      Data source
                    </p>
                    <h3 id="kpi-data-source-title" className="mt-1 text-lg font-semibold text-foreground">
                      {result?.linked ? result.programName : isLoading ? "Finding tracker" : "No linked tracker"}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {result?.linked
                        ? `${result.filterLabel}${result.year ? ` · ${result.year}` : ""} · ${locationName}`
                        : "Match this KPI to one of the available DHIS2 trackers."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Close"
                  >
                    <Icon name="X" size={18} />
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
                  {!isLoading && !error && result && !result.linked ? (
                    <p className="text-sm text-muted-foreground">
                      None of the available trackers match this KPI. The link uses the indicator wording, for example meetings, schools, health facilities, revenue, projects, permits, or the Annual Action Plan.
                    </p>
                  ) : null}

                  {isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Loader2" className="h-4 w-4 animate-spin" />
                      Loading tracker records...
                    </div>
                  ) : null}

                  {error ? <p className="text-sm text-destructive">{error}</p> : null}

                  {!isLoading && !error && result?.linked ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#16325a]/10 px-3 py-1 text-xs font-semibold text-[#16325a]">
                          {result.summary?.total || 0} records
                        </span>
                        <span className="text-sm text-muted-foreground">{result.programName}</span>
                      </div>
                      <p className="text-sm text-foreground">{result.summary?.detail}</p>
                      {result.summary?.quarters ? (
                        <div className="grid grid-cols-4 gap-2">
                          {Object.entries(result.summary.quarters).map(([quarter, count]) => (
                            <div key={quarter} className="rounded-xl border border-border px-3 py-2">
                              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{quarter}</p>
                              <p className="text-lg font-semibold text-foreground">{count}</p>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {result.rows.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                          No matching records were found for this organisation unit and year.
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded-xl border border-border">
                          <table className="min-w-full text-left text-sm">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                              <tr>
                                {columns.map((column) => (
                                  <th key={column.label} className="px-3 py-2 font-semibold">
                                    {column.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {result.rows.slice(0, 80).map((row) => (
                                <tr key={row.id} className="border-t border-border">
                                  {columns.map((column) => (
                                    <td key={column.label} className="max-w-xs px-3 py-2 align-top text-foreground">
                                      {formatCell(row.cells[column.label])}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {result.rows.length > 80 ? (
                            <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
                              Showing 80 of {result.rows.length} records. Expand to see the full filtered list.
                            </p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-4">
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    Close
                  </Button>
                  {result?.linked ? (
                    <Button type="button" onClick={openTrackers} disabled={isLoading || Boolean(error)}>
                      <Icon name="Maximize2" className="h-4 w-4" />
                      Expand
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
